'use client';

import { BtnBox, Container, StyledButton } from './styles';
import { FC, useMemo } from 'react';
import { useTranslations } from 'next-intl';

import { useAppContext } from '@/contexts';
import {
  useNavigationObjectList,
  useIsMounted,
  readListingNavState,
  writeListingNavState,
} from '@/hooks';
import { useRouter } from 'next/navigation';
import { PATHS } from '@/constants';
import { OfferType } from '@/types';
import { NavigationArrowLeft, NavigationArrowRight, SwiperArrowLeft } from '@/components';
import { Breadcrumbs as BreadcrumbsWidget } from '@/widgets/Breadcrumbs';

type SearchCardsNavigationProps = {
  currentId: string;
  offerType: OfferType;
  isSell: boolean;
  isProject?: boolean;
  breadcrumbsTitle?: string;
};

export const Navigation: FC<SearchCardsNavigationProps> = ({
  currentId,
  offerType,
  isSell,
  isProject = false,
  breadcrumbsTitle,
}) => {
  const t = useTranslations();
  const isMounted = useIsMounted();

  const router = useRouter();

  const { objectList } = useAppContext();

  const navState = isMounted ? readListingNavState() : null;
  const backUrl =
    navState && String(navState.objectId) === String(currentId) ? navState.listUrl : null;
  const isFromHousingSearch = !!backUrl && /\/(housing|projects)(\/|\?|$)/.test(backUrl);

  const { isLoading } = useNavigationObjectList(backUrl);

  const currentIndex = useMemo(() => {
    return objectList.findIndex((obj) => obj.id === currentId);
  }, [objectList, currentId]);

  const prevObject = currentIndex > 0 ? objectList[currentIndex - 1] : null;
  const nextObject =
    currentIndex >= 0 && currentIndex < objectList.length - 1 ? objectList[currentIndex + 1] : null;

  const basePath = isProject ? PATHS.PROJECT : 'housing';

  const goPrev = () => {
    if (!prevObject) return;
    const slugLink = `${prevObject.slug}__${prevObject.id}`;
    if (backUrl) writeListingNavState(backUrl, prevObject.id);
    router.push(`/${basePath}/${slugLink}`);
  };

  const goNext = () => {
    if (!nextObject) return;
    const slugLink = `${nextObject.slug}__${nextObject.id}`;
    if (backUrl) writeListingNavState(backUrl, nextObject.id);
    router.push(`/${basePath}/${slugLink}`);
  };

  const goToSearchList = () => {
    if (!backUrl) return;
    const url = new URL(backUrl, window.location.origin);
    url.searchParams.set('fromDetailId', currentId);
    url.searchParams.set('openedProperty', currentId);
    router.push(`${url.pathname}${url.search}`, { scroll: false });
  };

  const backLabel = isProject
    ? t('navigation.projects')
    : isSell
      ? t('navigation.sell')
      : t('navigation.rent');
  const backHref = isProject ? `/${PATHS.PROJECTS}` : `/${PATHS.HOUSING}?offerType=${offerType}`;

  if (isMounted && backUrl && isFromHousingSearch) {
    return (
      <>
        {breadcrumbsTitle && (
          <BreadcrumbsWidget
            breadcrumbsTitle={breadcrumbsTitle}
            backHref={backHref}
            backLabel={backLabel}
          />
        )}
        <Container>
          <StyledButton
            variant="outline-primary"
            sx={{ gap: '8px !important' }}
            startIcon={<SwiperArrowLeft />}
            onClick={goToSearchList}
          >
            {t('housingCardDetails.backToSearch')}
          </StyledButton>

          <BtnBox>
            <StyledButton
              disabled={isLoading || !prevObject}
              onClick={goPrev}
              sx={{
                padding: '6px 12px 6px 4px',
              }}
              variant="primary"
              startIcon={<NavigationArrowLeft disabled={isLoading || !prevObject} />}
            >
              {t('housingCardDetails.prev')}
            </StyledButton>
            <StyledButton
              disabled={isLoading || !nextObject}
              onClick={goNext}
              sx={{
                padding: '6px 4px 6px 12px',
              }}
              variant="primary"
              endIcon={<NavigationArrowRight disabled={isLoading || !nextObject} />}
            >
              {t('housingCardDetails.next')}
            </StyledButton>
          </BtnBox>
        </Container>
      </>
    );
  }

  return (
    <BreadcrumbsWidget
      breadcrumbsTitle={breadcrumbsTitle}
      backHref={backHref}
      backLabel={backLabel}
    />
  );
};
